'use client';
import React, { useState } from 'react';
import { XMarkIcon, ArrowDownTrayIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { usePWAInstall } from '@/hooks/usePWAInstall';

interface PWAInstallPromptProps {
  onDismiss?: () => void;
}

export default function PWAInstallPrompt({ onDismiss }: PWAInstallPromptProps) {
  const { canInstall, isInstalled, triggerInstall } = usePWAInstall();
  const [dismissed, setDismissed] = useState(false);
  const [installing, setInstalling] = useState(false);

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  const handleInstall = async () => {
    setInstalling(true);
    await triggerInstall();
    setInstalling(false);
  };

  // Don't show if: already installed, dismissed, or no install prompt available
  if (isInstalled || dismissed) return null;

  // For iOS (Safari) — show manual instructions since beforeinstallprompt isn't supported
  const isIOS = typeof navigator !== 'undefined' &&
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !(window as unknown as { MSStream?: unknown }).MSStream;

  if (!canInstall && !isIOS) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 max-w-sm mx-auto animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-card border border-border rounded-2xl shadow-2xl p-4 relative overflow-hidden">
        {/* Accent gradient top bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent via-blue-500 to-accent rounded-t-2xl" />

        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Dismiss"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
            <DevicePhoneMobileIcon className="w-5 h-5 text-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">Install TUGA Recruitment</p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              {isIOS
                ? 'Tap the Share button then "Add to Home Screen" to install the app.' :'Add to your home screen for quick access to your profile and job matches.'}
            </p>
          </div>
        </div>

        {!isIOS && (
          <button
            onClick={handleInstall}
            disabled={installing}
            className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 btn-navy rounded-xl text-sm font-medium disabled:opacity-70 transition-all"
          >
            {installing ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Installing...
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="w-4 h-4" />
                Install App
              </>
            )}
          </button>
        )}

        {isIOS && (
          <div className="mt-3 flex items-center gap-2 bg-muted/50 rounded-xl px-3 py-2">
            <span className="text-lg">⬆️</span>
            <p className="text-xs text-muted-foreground">
              Tap <strong className="text-foreground">Share</strong> → <strong className="text-foreground">Add to Home Screen</strong>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
